// Dependencies
import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { renderToStaticMarkup } from "react-dom/server";
import debounce from "lodash.debounce";

// Components
import App from "App";

// DIBK Design
import { Button, Container, Dialog, ErrorBox, Header, InfoBox, LoadingAnimation, Paper, Textarea } from "dibk-design";

// Partials
import Ansvarsrett from "components/partials/Forms/Ansvarsrett";
import KontrollErklaering from "components/partials/Forms/KontrollErklaering";
import SamsvarsErklaering from "components/partials/Forms/SamsvarsErklaering";
import RequestErrorDialog from "components/partials/RequestErrorDialog";

// Actions
import { fetchSubmission } from "actions/SubmissionActions";
import { fetchSelectedForm, rejectSelectedForm, saveSelectedForm, updateSelectedForm, updateSelectedFormEtag } from "actions/FormActions";
import { initateSigning, removeSigningProcess } from "actions/SigningActions";
import { convertSelectedFormToPDF } from "actions/PrintActions";
import { updateIsSavingData } from "actions/IsSavingDataActions";
import { updateIsEdited } from "actions/IsEditedActions";
import { updateLoadingMessage } from "actions/LoadingMessageActions";
import { renewLastInteractionAt } from "actions/LastInteractionAtActions";
import { showSnackbarMessage } from "actions/SnackbarActions";

import {
    // Alle skjemaer
    updateIsValidated,
    validateAnsvarligForetakKontaktpersonEpost,
    validateAnsvarligForetakKontaktpersonNavn,
    validateAnsvarligForetakKontaktpersonTelefonnummer,
    // Erklæring om ansvarsrett
    validateBeskrivelseForAllAnsvarsomraader,
    validateTiltaksklasseForAllAnsvarsomraader,
    validateDekkesOmradetAvSentralGodkjenningForAllAnsvarsomraader,
    validateSamsvarKontrollCheckboxesForAllAnsvarsomraader,
    validateErklaeringCheckboxes,
    // Samsvarserklæring
    validateAnsvarsrettProsjekterendeCheckboxes,
    validateAnsvarsomraadetAvsluttet,
    validateAnsvarsrettUtfoerendeTillatelse,
    validateMidlertidigBrukstillatelseGjennstaaendeInnenfor,
    validateHarTilstrekkeligSikkerhet,
    validateTypeArbeider,
    validateUtfoertInnen,
    validateErklaeringSamsvarCheckboxes,
    // Kontrollerklæring
    validateAnsvarsrettKontrollerendeRadioButtons,
    validateAttachment,
    getValidationMessageSummary,
    validateErklaeringKontrollCheckbox
} from "actions/ValidationActions";

// Stylesheets
import commonStyle from "components/routes/common.module.scss";
import style from "components/routes/Forms/FormType/Form.module.scss";

/* eslint import/no-webpack-loader-syntax: off */
import printStyle from "!!raw-loader!sass-loader!../../../../print.scss";

// Autosave-tidskonstanter (modulnivå så de er stabile mht. useMemo/useCallback-avhengigheter).
const SAVE_DEBOUNCE_MS = 2000; // Vent til brukeren tar en liten pause før lagring.
const SAVE_MAX_WAIT_MS = 10000; // Men lagre minst hvert 10. sekund ved sammenhengende redigering.

const Form = ({ showRejectDialog }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();

    // Redux store
    const loadingMessage = useSelector((state) => state.loadingMessage);
    const selectedSubmission = useSelector((state) => state.selectedSubmission);
    const selectedForm = useSelector((state) => state.selectedForm);
    const oidc = useSelector((state) => state.oidc);
    const isEdited = useSelector((state) => state.isEdited);
    const isSavingData = useSelector((state) => state.isSavingData);
    const isValidated = useSelector((state) => state.isValidated);
    const validationMessages = useSelector((state) => state.validationMessages);

    // State
    const [isLoaded, setIsLoaded] = useState();
    const [rejectionMessage, setRejectionMessage] = useState();
    const [showHarAktivSigneringsjobbDialog, setShowHarAktivSigneringsjobbDialog] = useState();
    const [requestError, setRequestError] = useState();
    const [saveStatus, setSaveStatus] = useState(null); // null | "saving" | "saved" | "error"

    // Refs holding the latest values so the save callback stays stable (not recreated per keystroke)
    const isEditedRef = useRef(isEdited);
    const isSavingDataRef = useRef(isSavingData);
    const selectedFormRef = useRef(selectedForm);
    const saveAbortRef = useRef(null);
    const saveRetryCountRef = useRef(0);
    const saveRetryTimerRef = useRef(null);

    useEffect(() => {
        isEditedRef.current = isEdited;
    }, [isEdited]);
    useEffect(() => {
        isSavingDataRef.current = isSavingData;
    }, [isSavingData]);
    useEffect(() => {
        selectedFormRef.current = selectedForm;
    }, [selectedForm]);

    const MAX_SAVE_RETRIES = 5;

    // Params
    const submissionId = params.submissionId;

    const fetchFormData = useCallback(
        (submissionId) => {
            dispatch(updateLoadingMessage("Henter innsending"));
            return dispatch(fetchSubmission(submissionId))
                .then((fetchedSubmission) => {
                    dispatch(updateLoadingMessage("Henter skjema"));
                    if (fetchedSubmission && Object.keys(fetchedSubmission).length) {
                        return dispatch(fetchSelectedForm(fetchedSubmission))
                            .then((fetchedForm) => {
                                dispatch(updateLoadingMessage(null));
                                return fetchedForm;
                            })
                            .catch((error) => {
                                console.log("fetchSelectedForm", error);
                                dispatch(updateLoadingMessage(null));
                            });
                    }
                })
                .catch((error) => {
                    console.log("fetchSubmission", error);
                    dispatch(updateLoadingMessage(null));
                });
        },
        [dispatch]
    );

    const handleValidate = (validations) => {
        if (validations) {
            validations();
        }
    };

    const handleApiResponseStatus = useCallback(
        (status, selectedForm, xCorrelationId, requestMethod) => {
            if (status === 409) {
                // Conflict - aktiv signeringsjobb
                setShowHarAktivSigneringsjobbDialog(true);
            } else if (status === 412) {
                // Samtidighetskonflikt: erklæringen er endret et annet sted (annen fane / server).
                // Last inn siste versjon IN-PLACE - uten å sette isLoaded=false, som ville avmontert
                // skjematreet og trigget en krasj i DragAndDropFileInput sin unmount-opprydding
                // (grå skjerm). Loading-overlayet fra fetchFormData dekker innlastingen. Nullstiller
                // dirty-flagget siden de lokale endringene erstattes av serverens versjon.
                dispatch(showSnackbarMessage("Erklæringen ble endret et annet sted. Laster inn siste versjon.", 5000));
                dispatch(updateIsEdited(false));
                setSaveStatus(null);
                fetchFormData(submissionId);
            } else {
                // 400/401/404/500 og øvrige feil: ikke naviger vekk fra skjemaet (det ville
                // forkastet endringer som ikke er lagret). Vis feildialog med mulighet til å prøve
                // igjen, slik at brukerens input beholdes.
                setRequestError({
                    requestMethod,
                    xCorrelationId
                });
            }
        },
        [dispatch, fetchFormData, submissionId]
    );

    const saveForm = useCallback(
        (formToSave, accessToken) => {
            // Abort any in-flight save and cancel any pending retry before starting a new one.
            if (isSavingDataRef.current) {
                saveAbortRef.current?.abort();
            }
            if (saveRetryTimerRef.current) {
                clearTimeout(saveRetryTimerRef.current);
                saveRetryTimerRef.current = null;
            }

            dispatch(updateIsSavingData(true));
            setSaveStatus("saving");

            const selectedFormSaveRequest = dispatch(saveSelectedForm(formToSave, accessToken));
            saveAbortRef.current = selectedFormSaveRequest.abortController;

            selectedFormSaveRequest.ready.then((response) => {
                // Superseded by a newer save - that request now owns the flow.
                if (response?.aborted) {
                    return;
                }

                dispatch(updateIsSavingData(false));

                if (response?.status === 200) {
                    saveRetryCountRef.current = 0;
                    setSaveStatus("saved");

                    // Did the user make new edits while this save was in flight? Every edit replaces
                    // the selectedForm object, so reference-equality tells us whether the store still
                    // holds exactly what we saved.
                    const noNewEditsSinceSave = selectedFormRef.current === formToSave;

                    if (noNewEditsSinceSave) {
                        // Safe to mark clean and reconcile with the server's canonical response
                        // (fresh _links/etag/normalised values). Preserve the client-side
                        // kommunenavn enrichment that fetchSelectedForm adds.
                        dispatch(updateIsEdited(false));
                        if (response.data) {
                            const reconciled = response.data;
                            const kommunenavn = formToSave?.formData?.eiendomByggesteder?.[0]?.kommunenavn;
                            if (kommunenavn && reconciled?.formData?.eiendomByggesteder?.[0]) {
                                reconciled.formData.eiendomByggesteder[0].kommunenavn = kommunenavn;
                            }
                            dispatch(updateSelectedForm(reconciled));
                        }
                    } else if (response.data?.etag) {
                        // New edits arrived during the save: keep the form dirty (the autosave effect
                        // will persist them), but refresh only the etag so the next save doesn't get a
                        // false 412 from carrying the now-stale token.
                        dispatch(updateSelectedFormEtag(response.data.etag));
                    }
                    return;
                }

                // Transient failure (network error or 5xx): keep the form dirty and retry with
                // exponential backoff, so a dropped connection never silently loses the edit.
                const isTransient = response?.networkError || response?.status === 0 || response?.status >= 500;
                if (isTransient) {
                    if (saveRetryCountRef.current < MAX_SAVE_RETRIES) {
                        const delay = Math.min(30000, 1000 * 2 ** saveRetryCountRef.current);
                        saveRetryCountRef.current += 1;
                        setSaveStatus("error");
                        saveRetryTimerRef.current = setTimeout(() => {
                            saveForm(formToSave, accessToken);
                        }, delay);
                    } else {
                        // Give up auto-retrying; surface a hard error with a manual retry.
                        setSaveStatus("error");
                        setRequestError({
                            requestMethod: () => {
                                saveRetryCountRef.current = 0;
                                saveForm(formToSave, accessToken);
                            },
                            xCorrelationId: response?.xCorrelationId
                        });
                    }
                    return;
                }

                // Non-transient (400/401/404/409): keep the edit, surface via handleApiResponseStatus.
                setSaveStatus("error");
                handleApiResponseStatus(response?.status, formToSave, response?.xCorrelationId, () => {
                    saveRetryCountRef.current = 0;
                    saveForm(formToSave, accessToken);
                });
            });
        },
        [dispatch, handleApiResponseStatus]
    );

    const debouncedSave = useMemo(
        () =>
            debounce(
                (formToSave, accessToken) => {
                    saveForm(formToSave, accessToken);
                },
                SAVE_DEBOUNCE_MS,
                // maxWait bounds the unsaved window: even during continuous editing (blurring field
                // after field within 2s), a save fires at least every SAVE_MAX_WAIT_MS instead of the
                // debounce resetting indefinitely.
                { maxWait: SAVE_MAX_WAIT_MS }
            ),
        [saveForm]
    );

    useEffect(() => {
        const accessToken = oidc?.user?.access_token;

        if (isEdited) {
            debouncedSave(selectedForm, accessToken);
            dispatch(renewLastInteractionAt());
        }
    }, [isEdited, selectedForm, oidc, debouncedSave, dispatch]);

    // On unmount / navigation: flush (not cancel) any pending debounced save, so an edit made
    // within the 2s debounce window isn't silently dropped when the user leaves the form via
    // in-app (React Router) navigation - the beforeunload guard only covers full-page close.
    // The save's dispatch/fetch complete independently of the component; in React 18 the handler's
    // post-unmount setState is a harmless no-op.
    useEffect(() => {
        return () => {
            if (saveRetryTimerRef.current) {
                clearTimeout(saveRetryTimerRef.current);
            }
            debouncedSave.flush();
        };
    }, [debouncedSave]);

    // Warn before leaving with unsaved or in-flight changes (no data is persisted anywhere).
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isEdited || isSavingData) {
                event.preventDefault();
                event.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isEdited, isSavingData]);

    const redirectIfNotValidStatus = useCallback(
        (formData) => {
            const validStatuses = ["tilSignering", "iArbeid"];
            const isValidStatus = validStatuses.some((status) => {
                return formData?.status === status;
            });
            if (!isValidStatus) {
                navigate(`/skjema/${formData.referanseId}`);
            }
        },
        [navigate]
    );

    // Redirect if not authenticated
    useEffect(() => {
        if (!oidc?.user) {
            navigate(`/skjema/${submissionId}/utlogget`);
        }
    }, [navigate, oidc?.user, submissionId]);

    useEffect(() => {
        const isAuthenticated = oidc?.user && Object.keys(oidc.user).length;
        if (isAuthenticated) {
            if (!selectedForm || !Object.keys(selectedForm).length) {
                fetchFormData(submissionId).then((fetchedForm) => {
                    redirectIfNotValidStatus(fetchedForm);
                    setIsLoaded(true);
                });
            } else {
                redirectIfNotValidStatus(selectedForm);
            }
        }
    }, [selectedForm, oidc, navigate, fetchFormData, submissionId, redirectIfNotValidStatus]);

    const renderForm = (formType, selectedSubmission) => {
        switch (formType) {
            case "ansvarsrett":
                return (
                    <Ansvarsrett
                        selectedSubmission={selectedSubmission}
                        validationHandler={(validations) => handleValidate(validations)}
                    />
                );
            case "kontrollerklaering":
                return (
                    <KontrollErklaering
                        selectedSubmission={selectedSubmission}
                        validationHandler={(validations) => handleValidate(validations)}
                        fetchFormData={(submissionId) => fetchFormData(submissionId)}
                        setRequestError={(requestError) => setRequestError(requestError)}
                    />
                );
            case "samsvarserklaering":
                return (
                    <SamsvarsErklaering
                        selectedSubmission={selectedSubmission}
                        validationHandler={(validations) => handleValidate(validations)}
                    />
                );
            default:
                return "";
        }
    };

    const renderHtmlContentForPdf = () => {
        sessionStorage.print = "true";
        const htmlString = renderToStaticMarkup(
            <div className="page signed-document">
                <App />
            </div>
        );
        sessionStorage.print = "false";
        const htmlContentString = `<html><head><style>${printStyle.replace(
            /\r?\n|\r/g,
            ""
        )}</style></head><body>${htmlString}</body></html>`;
        return htmlContentString;
    };

    const handleSigningButtonClick = () => {
        if (selectedForm?.signeringsUrl?.length) {
            window.location.href = selectedForm?.signeringsUrl;
        } else {
            dispatch(updateLoadingMessage("Genererer PDF-fil"));

            const accessToken = oidc?.user?.access_token;
            const stage = "signing_preparation";
            const selectedFormSaveRequest = dispatch(saveSelectedForm(selectedForm, accessToken, stage));

            selectedFormSaveRequest.ready.then((response) => {
                if (response?.status !== 200 || !response.data) {
                    dispatch(updateLoadingMessage(null));
                    handleApiResponseStatus(
                        response?.status,
                        selectedForm,
                        response?.xCorrelationId,
                        handleSigningButtonClick
                    );
                    return;
                }
                dispatch(updateSelectedForm(response.data)).then(() => {
                    const htmlContentForPdf = renderHtmlContentForPdf();
                    dispatch(convertSelectedFormToPDF(htmlContentForPdf, selectedSubmission.referanseId, accessToken))
                        .then(() => {
                            dispatch(updateLoadingMessage("Klargjør signering"));
                            dispatch(initateSigning(selectedSubmission.referanseId, accessToken))
                                .then((response) => {
                                    dispatch(updateLoadingMessage(null));
                                    if (response.status === 200) {
                                        let signingUrl = response.data.signingUrl;
                                        signingUrl += `?skjema=${selectedSubmission.referanseId}`;
                                        window.location.href = signingUrl;
                                    } else {
                                        handleApiResponseStatus(
                                            response.status,
                                            selectedForm,
                                            response.xCorrelationId,
                                            handleSigningButtonClick
                                        );
                                    }
                                })
                                .catch((error) => {
                                    dispatch(updateLoadingMessage(null));
                                });
                        })
                        .catch((error) => {
                            dispatch(updateLoadingMessage(null));
                        });
                });
            });
        }
    };

    const handleClickOutsideRejectDialog = () => {
        setRejectionMessage(null);
        navigate(`/skjema/${submissionId}/rediger`);
    };

    const handleSubmitRejectionButtonClick = () => {
        const accessToken = oidc?.user?.access_token;
        dispatch(rejectSelectedForm(selectedForm, rejectionMessage, accessToken)).then((response) => {
            if (response.status === 200) {
                navigate(`/skjema/${selectedForm.referanseId}/signatur-avvist`);
            } else {
                handleApiResponseStatus(
                    response.status,
                    selectedForm,
                    response.xCorrelationId,
                    handleSubmitRejectionButtonClick
                );
            }
        });
    };

    const handleClickOutsideHarAktivSigneringsjobbDialog = () => {
        const scrollPositionBeforeReload = document.getElementById("main-content").scrollTop;
        setIsLoaded(false);
        fetchFormData(submissionId).then(() => {
            setIsLoaded(true);
            setShowHarAktivSigneringsjobbDialog(false);
            setTimeout(() => {
                document.getElementById("main-content").scrollTop = scrollPositionBeforeReload;
            }, 100);
        });
    };

    const handleSubmitHarAktivSigneringsjobbButtonClick = () => {
        const accessToken = oidc?.user?.access_token;
        setShowHarAktivSigneringsjobbDialog(false);
        dispatch(removeSigningProcess(selectedForm, accessToken)).then(() => {
            saveForm(selectedForm, accessToken);
        });
    };

    const handleClickOutsideRequestErrorDialog = () => {
        setRequestError(null);
    };

    const runValidations = (formType) => {
        switch (formType) {
            case "ansvarsrett":
                dispatch(validateAnsvarligForetakKontaktpersonEpost());
                dispatch(validateAnsvarligForetakKontaktpersonNavn());
                dispatch(validateAnsvarligForetakKontaktpersonTelefonnummer());
                dispatch(validateErklaeringCheckboxes());
                dispatch(validateBeskrivelseForAllAnsvarsomraader());
                dispatch(validateTiltaksklasseForAllAnsvarsomraader());
                dispatch(validateDekkesOmradetAvSentralGodkjenningForAllAnsvarsomraader());
                dispatch(validateSamsvarKontrollCheckboxesForAllAnsvarsomraader());
                break;
            case "samsvarserklaering":
                dispatch(validateAnsvarligForetakKontaktpersonEpost());
                dispatch(validateAnsvarligForetakKontaktpersonNavn());
                dispatch(validateAnsvarligForetakKontaktpersonTelefonnummer());
                dispatch(validateAnsvarsrettProsjekterendeCheckboxes());
                dispatch(validateAnsvarsomraadetAvsluttet());
                dispatch(validateAnsvarsrettUtfoerendeTillatelse());
                dispatch(validateMidlertidigBrukstillatelseGjennstaaendeInnenfor());
                dispatch(validateHarTilstrekkeligSikkerhet());
                dispatch(validateTypeArbeider());
                dispatch(validateUtfoertInnen());
                dispatch(validateErklaeringSamsvarCheckboxes());
                break;
            case "kontrollerklaering":
                dispatch(validateAnsvarligForetakKontaktpersonEpost());
                dispatch(validateAnsvarligForetakKontaktpersonNavn());
                dispatch(validateAnsvarligForetakKontaktpersonTelefonnummer());
                dispatch(validateAnsvarsomraadetAvsluttet());
                dispatch(validateAnsvarsrettKontrollerendeRadioButtons());
                dispatch(validateAttachment());
                dispatch(validateErklaeringKontrollCheckbox());
                break;
            default:
                return;
        }
    };

    const renderSaveStatusMessage = () => {
        if (saveStatus === "saving" || isSavingData) return "Lagrer endringer …";
        if (saveStatus === "error") return "Kunne ikke lagre endringene – prøver igjen …";
        if (isEdited) return "Du har endringer som ikke er lagret";
        if (saveStatus === "saved") return "Alle endringer er lagret";
        return null;
    };

    const saveStatusMessage = renderSaveStatusMessage();

    return selectedSubmission && isLoaded && oidc?.user ? (
        <Container>
            <Paper>
                {renderForm(selectedSubmission?.innsendingstype, selectedSubmission)}
                <section>
                    <div className={style.signingInfoContainer}>
                        {validationMessages && Object.keys(validationMessages)?.length ? (
                            <div className={commonStyle.marginBottomSmall} aria-live="polite" role="alert">
                                <ErrorBox>
                                    <div>
                                        <h2 className={commonStyle.boxTitle}>
                                            Du kan ikke signere erklæringen før alle opplysningene er fylt ut:
                                        </h2>
                                        <ul className={commonStyle.boxList}>
                                            {dispatch(getValidationMessageSummary()).map((validationMessage, index) => {
                                                return <li key={index}>{validationMessage}</li>;
                                            })}
                                        </ul>
                                    </div>
                                </ErrorBox>
                            </div>
                        ) : null}
                        {isValidated && !Object.keys(validationMessages)?.length ? (
                            <InfoBox>
                                Når du trykker Til signering, blir du sendt til Postens tjeneste for signering. Der kan
                                du se gjennom og signere ved å logge inn på nytt. Den signerte erklæringen sendes
                                automatisk til ansvarlig søker med kopi til e-postadressen du har oppgitt.
                            </InfoBox>
                        ) : null}
                    </div>

                    {isValidated ? (
                        <Button
                            content="Til signering"
                            color="primary"
                            disabled={
                                (validationMessages && Object.keys(validationMessages)?.length ? true : false) ||
                                isEdited ||
                                isSavingData
                            }
                            onClick={() => {
                                handleSigningButtonClick();
                                dispatch(renewLastInteractionAt());
                            }}
                        />
                    ) : (
                        <Button
                            content="Kontroller"
                            color="primary"
                            disabled={isEdited || isSavingData}
                            onClick={() => {
                                runValidations(selectedSubmission?.innsendingstype);
                                dispatch(updateIsValidated(true));
                                dispatch(renewLastInteractionAt());
                            }}
                        />
                    )}

                    {saveStatusMessage ? (
                        <div className={commonStyle.marginTop} aria-live="polite" role="status">
                            {saveStatusMessage}
                        </div>
                    ) : null}

                    {loadingMessage?.length ? <LoadingAnimation fixed message={loadingMessage} /> : ""}
                    <div className={commonStyle.marginTop}>
                        <Header size={5} htmlTag="h2" content="Har du oppdaget noe feil i erklæringen?" />
                        <Link
                            to={`/skjema/${submissionId}/avvis`}
                            title="Avvis erklæring"
                            onClick={() => {
                                dispatch(renewLastInteractionAt());
                            }}
                        >
                            Meld fra til ansvarlig søker
                        </Link>
                    </div>
                </section>
            </Paper>
            {showRejectDialog ? (
                <Dialog
                    onClickOutside={() => {
                        handleClickOutsideRejectDialog();
                        dispatch(renewLastInteractionAt());
                    }}
                    closeButton
                    maxWidth="960px"
                >
                    <Header content="Meld inn feil til ansvarlig søker" size={2} />
                    <p>
                        Skriv en begrunnelse i tekstboksen under. Ansvarlig søker må rette opp feilen og sende en ny
                        lenke for signering.
                    </p>
                    <Textarea
                        id="rejectionMessage"
                        defaultValue={rejectionMessage}
                        onChange={(event) => {
                            dispatch(renewLastInteractionAt());
                            const submitRejectionButton = document.getElementById("submitRejectionButton");
                            const disabledSubmitRejectionButton = !event?.target?.value?.trim()?.length > 0;
                            submitRejectionButton.disabled = disabledSubmitRejectionButton;
                        }}
                        onBlur={(event) => {
                            dispatch(renewLastInteractionAt());
                            setRejectionMessage(event.target.value);
                        }}
                        resize="vertical"
                    />
                    <div className={`${commonStyle.marginTop} ${commonStyle.marginBottomSmall}`}>
                        <Button
                            id="submitRejectionButton"
                            content="Avvis erklæring"
                            onClick={() => {
                                dispatch(renewLastInteractionAt());
                                handleSubmitRejectionButtonClick();
                            }}
                            color="primary"
                            disabled={!rejectionMessage?.trim()?.length}
                        />
                    </div>
                    <p>
                        <Link
                            to={`/skjema/${submissionId}/rediger`}
                            title="Avvis erklæring"
                            onClick={() => {
                                dispatch(renewLastInteractionAt());
                            }}
                        >
                            Avbryt og gå tilbake til erklæringen.
                        </Link>
                    </p>
                </Dialog>
            ) : null}
            {showHarAktivSigneringsjobbDialog ? (
                <Dialog
                    onClickOutside={() => {
                        handleClickOutsideHarAktivSigneringsjobbDialog();
                        dispatch(renewLastInteractionAt());
                    }}
                    closeButton
                    maxWidth="960px"
                >
                    <Header content="Erklæringen har allerede blitt sendt til signering" size={2} />
                    <p>
                        Denne erklæringen har tidligere blitt sendt til signering, men signeringen ble ikke fullført.
                        Vil du avbryte den andre signeringsjobben og fortsette redigering?
                    </p>
                    <div className={`${commonStyle.marginTop} ${commonStyle.marginBottomSmall}`}>
                        <Button
                            content="Ja, fortsett redigering"
                            onClick={handleSubmitHarAktivSigneringsjobbButtonClick}
                            color="primary"
                        />
                    </div>
                    <p>
                        <button
                            className={commonStyle.link}
                            onClick={() => {
                                handleClickOutsideHarAktivSigneringsjobbDialog();
                                dispatch(renewLastInteractionAt());
                            }}
                        >
                            Avbryt og tilbakestill endringer
                        </button>
                    </p>
                </Dialog>
            ) : null}
            {requestError ? (
                <RequestErrorDialog
                    requestError={requestError}
                    clickOutsideHandler={() => {
                        handleClickOutsideRequestErrorDialog();
                        dispatch(renewLastInteractionAt());
                    }}
                />
            ) : null}
        </Container>
    ) : !oidc?.user || !Object.keys(oidc.user).length ? (
        "Du er utlogget"
    ) : null;
};

export default Form;
